<template>
  <div class="w-full p-6">
    <div class="text-center font-bold text-gray-800 text-2xl">
      Confirmation de la livraison
    </div>
    <div class="py-2"></div>

    <!-- Route Summary Map -->
    <div class="w-full h-[250px] relative bg-slate-100 rounded-xl overflow-hidden mb-4">
      <Map
        ref="mapRef"
        @ready="onMapReady"
        class="w-full h-full rounded-xl"
      />
    </div>

    <!-- Addresses -->
    <div>
      <div class="relative pl-[1px] flex flex-col gap-4 pt-4">
        <!-- Vertical line -->
        <div class="absolute left-3 top-0 bottom-0 w-px bg-slate-300"></div>

        <!-- Départ -->
        <div class="relative flex gap-4">
          <div class="relative z-10 w-6 flex justify-center">
            <span class="w-3 h-3 rounded-full bg-blue-600 mt-2"></span>
          </div>
          <div class="">
            <div class="font-bold text-gray-800">Départ</div>
            <div class="max-h-12 overflow-hidden text-gray-800">
              <div v-if="filledData?.srcDstLocation?.srcPosition">
                {{ filledData?.srcDstLocation?.srcPosition.locationText }}
              </div>
              <div v-else>Erreur de sélection</div>
            </div>
          </div>
        </div>

        <!-- Checkpoints -->
        <div
          v-for="(checkpoint, i) in filledData?.srcDstLocation?.checkpoints"
          :key="i"
          class="relative flex gap-4"
        >
          <div class="relative z-10 w-6 flex justify-center">
            <span class="w-3 h-3 rounded-full bg-slate-500 mt-2"></span>
          </div>
          <div class="">
            <div class="font-bold text-gray-800">Détour n°{{ i + 1 }}</div>
            <div class="max-h-12 overflow-hidden text-gray-800">
              <div v-if="checkpoint">
                {{ checkpoint.locationText }}
              </div>
              <div v-else>Erreur de sélection</div>
            </div>
          </div>
        </div>

        <!-- Arrivée -->
        <div class="relative flex gap-4">
          <div class="relative z-10 w-6 flex justify-center">
            <span class="w-3 h-3 rounded-full bg-green-600 mt-2"></span>
          </div>
          <div class="">
            <div class="font-bold text-gray-800">Arrivée</div>
            <div class="max-h-12 overflow-hidden text-gray-800">
              <div v-if="filledData?.srcDstLocation?.dstPosition">
                {{ filledData?.srcDstLocation?.dstPosition.locationText }}
              </div>
              <div v-else>Erreur de sélection</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-4"></div>

    <!-- Section Lien de Paiement Sécurisé (Escrow) -->
    <div class="px-0">
      <!-- Toggle escrow -->
      <button
        type="button"
        @click="toggleEscrow"
        class="w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all duration-300"
        :class="escrow.enabled
          ? 'border-primary bg-primary/5 shadow-md shadow-primary/10'
          : 'border-slate-200 bg-slate-50 hover:border-slate-300'"
      >
        <div
          class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
          :class="escrow.enabled ? 'bg-primary text-white' : 'bg-slate-200 text-slate-500'"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <p class="font-bold" :class="escrow.enabled ? 'text-primary' : 'text-gray-800'">
            Paiement sécurisé par Rapidoo
          </p>
          <p class="text-xs text-slate-500 mt-0.5">
            Activez le paiement pour que votre client vous paie sous a supervision de rapisoo
          </p>
        </div>
        <div
          class="w-11 h-6 rounded-full transition-colors flex-shrink-0"
          :class="escrow.enabled ? 'bg-primary' : 'bg-slate-300'"
        >
          <div
            class="w-5 h-5 bg-white rounded-full shadow-sm transition-transform mt-0.5"
            :class="escrow.enabled ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'"
          ></div>
        </div>
      </button>

      <!-- Escrow options (visible only when enabled) -->
      <div v-if="escrow.enabled" class="mt-4 space-y-4 p-4 bg-primary/5 rounded-2xl border border-primary/20">
        <!-- Merchandise price -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Prix de la marchandise (FCFA)
          </label>
          <input
            v-model.number="escrow.merchandisePrice"
            type="number"
            min="0"
            placeholder="Ex: 5000"
            @input="onMerchandisePriceInput"
            class="w-full px-4 py-3 rounded-xl border-2 border-slate-200 text-gray-800 font-medium text-base focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all"
          />
          <p class="text-xs text-slate-500 mt-1">Montant que votre client devra payer pour la marchandise</p>
        </div>

        <!-- Who pays delivery -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Frais de livraison supportés par
          </label>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              @click="setDeliveryFeePayer('receiver')"
              class="p-3 rounded-xl border-2 text-sm font-medium transition-all"
              :class="escrow.deliveryFeePayer === 'receiver'
                ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'"
            >
              L'acheteur
            </button>
            <button
              type="button"
              @click="setDeliveryFeePayer('sender')"
              class="p-3 rounded-xl border-2 text-sm font-medium transition-all"
              :class="escrow.deliveryFeePayer === 'sender'
                ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'"
            >
              Moi
            </button>
          </div>
          <p v-if="escrow.deliveryFeePayer === 'receiver'" class="text-xs text-primary mt-2 font-medium">
            ✓ L'acheteur paiera {{ (Number(escrow.merchandisePrice) || 0) + buyerDeliveryCost }} FCFA au total (marchandise + livraison)
          </p>
          <p v-else class="text-xs text-slate-500 mt-2">
            ✓ L'acheteur paiera uniquement {{ Number(escrow.merchandisePrice) || 0 }} FCFA (marchandise)
          </p>
        </div>
      </div>
    </div>

    <div class="py-4"></div>
    <!-- Coûts -->
    <div class="flex flex-col px-2 gap-3 relative">
      <!-- Loading indicator overlay when recalculating fees -->
      <div
        v-if="isCalculatingFees"
        class="absolute inset-0 bg-white/75 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10"
      >
        <div class="flex items-center gap-2 text-primary font-medium text-sm">
          <svg class="animate-spin w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Calcul des frais en cours...</span>
        </div>
      </div>

      <div class="flex justify-between">
        <div class="text-gray-800">Coût de livraison</div>
        <div class="font-bold text-gray-800">{{ deliveryCost }} FCFA</div>
      </div>
      <div class="flex justify-between text-sm text-gray-500">
        <div>Distance ({{ totalDistance }} km)</div>
        <div>{{ distanceCost }} FCFA</div>
      </div>
      <div class="flex justify-between text-sm text-gray-500" v-if="weightSurcharge > 0">
        <div>Supplément poids</div>
        <div>{{ weightSurcharge }} FCFA</div>
      </div>
      <div class="flex justify-between text-sm text-gray-500" v-if="feesData.documentFee > 0">
        <div>Frais documents</div>
        <div>{{ feesData.documentFee }} FCFA</div>
      </div>
      <div class="flex justify-between text-sm text-gray-500" v-if="feesData.checkpointFee > 0">
        <div>Frais détours</div>
        <div>{{ feesData.checkpointFee }} FCFA</div>
      </div>
      <div class="flex justify-between" v-if="paymentFee > 0">
        <div class="text-gray-800">Frais de paiement</div>
        <div class="text-gray-800">{{ paymentFee }} FCFA</div>
      </div>
      <div class="flex justify-between text-sm text-emerald-600 font-medium" v-if="feesData.discount > 0">
        <div>Remise</div>
        <div>-{{ feesData.discount }} FCFA</div>
      </div>

      <div class="border-t border-gray-200 my-1"></div>

      <!-- Sans escrow (Livraison classique) -->
      <template v-if="!escrow.enabled">
        <div class="flex justify-between font-bold text-xl text-gray-800">
          <div>Total à payer</div>
          <div>{{ totalAmount }} FCFA</div>
        </div>
      </template>

      <!-- Avec escrow (Lien de paiement sécurisé) -->
      <template v-else>
        <div class="flex justify-between font-bold text-xl text-gray-800">
          <div>Total à payer (par vous)</div>
          <div>{{ senderToPay }} FCFA</div>
        </div>
        <div class="flex justify-between text-sm text-slate-600">
          <div>Payé par l'acheteur</div>
          <div class="font-semibold text-slate-800">{{ buyerToPay }} FCFA</div>
        </div>
        <div class="flex justify-between">
          <div class="text-gray-800">Reste à recevoir</div>
          <div class="font-bold" :class="remainingAmount < 0 ? 'text-red-600' : 'text-emerald-600'">
            {{ remainingAmount }} FCFA
          </div>
        </div>
      </template>
    </div>
    <!-- Distance et durée -->
    <div class="py-4"></div>
    <div>
      <div class="text-lg px-2 font-bold text-gray-800">Trajet</div>
      <div class="flex flex-col gap-2">
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Distance totale</div>
          <div class="font-bold">
            {{ feesData.distanceFormatted || filledData?.fees?.distanceFormatted || '-' }}
          </div>
        </div>
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Durée estimée</div>
          <div class="font-bold">
            {{ feesData.durationFormatted || filledData?.fees?.durationFormatted || '-' }}
          </div>
        </div>
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Détours</div>
          <div class="font-bold">
            {{ filledData?.srcDstLocation?.checkpoints?.length || 0 }}
          </div>
        </div>
      </div>
    </div>

    <div class="py-4"></div>

    <!-- Livraison -->
    <div>
      <div class="text-lg px-2 font-bold text-gray-800">Livraison</div>
      <div class="flex flex-col gap-2">
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Date & Heure</div>
          <div class="font-bold">
            {{ formattedDate }} à {{ formattedTime }}
          </div>
        </div>
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Mode de livraison</div>
          <div class="font-bold">
            {{ filledData?.deliveryType?.deliveryType == 'car' ? 'Voiture' : 'Moto' }}
          </div>
        </div>
      </div>
    </div>

    <div class="py-4"></div>

    <!-- Détail du colis -->
    <div>
      <div class="text-lg px-2 font-bold text-gray-800">Détail du colis</div>
      <div class="flex flex-col gap-2">
        <div class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800">
          <div>Type de colis</div>
          <div class="font-bold">
            {{ filledData?.packageType?.type == 'document' ? 'Document' : 'Colis' }}
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'document'"
        >
          <div>Nombre de document</div>
          <div class="font-bold">
            {{ filledData?.packageType?.nbDocs || 0 }}
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'parcel'"
        >
          <div>Poids</div>
          <div class="font-bold">
            {{ parcelDetails.weight || 0 }} {{ parcelDetails.unit }}
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'parcel'"
        >
          <div>Longueur</div>
          <div class="font-bold">
            {{ parcelDetails.length || 0 }} cm
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'parcel'"
        >
          <div>Largeur</div>
          <div class="font-bold">
            {{ parcelDetails.width || 0 }} cm
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'parcel'"
        >
          <div>Hauteur</div>
          <div class="font-bold">
            {{ parcelDetails.height || 0 }} cm
          </div>
        </div>
        <div
          class="bg-slate-100 flex justify-between p-4 rounded-xl text-gray-800"
          v-if="filledData?.packageType?.type == 'parcel' || filledData?.packageType?.type == 'document'"
        >
          <div>Quantité</div>
          <div class="font-bold">
            {{ filledData?.packageType?.type == 'parcel' ? (parcelDetails.quantity || 1) : (filledData?.packageType?.nbDocs || 1) }}
          </div>
        </div>
      </div>
    </div>

    <div class="py-4"></div>

  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Map from '@features/map/Map.vue';

const mapRef = ref(null);
const mapReady = ref(false);
const isCalculatingFees = ref(false);
let debounceTimer = null;

const props = defineProps({
  filledData: {
    type: Object,
    default() {
      return {};
    }
  }
});

// Escrow / Lien de paiement sécurisé
const escrow = ref({
  enabled: Boolean(props.filledData?.paymentLinkEnabled),
  merchandisePrice: Number(props.filledData?.merchandisePrice) || 0,
  deliveryFeePayer: props.filledData?.deliveryFeePayer || 'receiver' // 'receiver' | 'sender'
});

// Local refs for fees (reactive to changes)
const feesData = ref({
  baseFee: 0,
  pickupFee: 0,
  distanceFee: 0,
  weightFee: 0,
  documentFee: 0,
  checkpointFee: 0,
  originalDeliveryFee: 0,
  deliveryFee: 0,
  serviceFee: 0,
  discount: 0,
  customerDeliveryCost: 0,
  merchantDeliveryCost: 0,
  merchantPayout: 0,
  total: 0,
  totalAmount: 0,
  totalDistance: 0,
  totalDuration: 0,
  distanceFormatted: '-',
  durationFormatted: '-',
  distanceCost: 0,
  weightSurcharge: 0,
  deliveryCost: 0,
  paymentFee: 0,
  currency: 'XOF'
});

function updateFees(newFees) {
  if (!newFees) return;
  console.log('[SummaryStep] Updating feesData with:', newFees);
  feesData.value = {
    baseFee: newFees.baseFee || 0,
    pickupFee: newFees.pickupFee || 0,
    distanceFee: newFees.distanceFee || 0,
    weightFee: newFees.weightFee || 0,
    documentFee: newFees.documentFee || 0,
    checkpointFee: newFees.checkpointFee || 0,
    originalDeliveryFee: newFees.originalDeliveryFee || 0,
    deliveryFee: newFees.deliveryFee || 0,
    serviceFee: newFees.serviceFee || 0,
    discount: newFees.discount || 0,
    customerDeliveryCost: newFees.customerDeliveryCost || 0,
    merchantDeliveryCost: newFees.merchantDeliveryCost || 0,
    merchantPayout: newFees.merchantPayout || 0,
    total: newFees.total ?? newFees.totalAmount ?? 0,
    totalAmount: newFees.total ?? newFees.totalAmount ?? 0,
    totalDistance: newFees.totalDistance || newFees.distanceKm || 0,
    totalDuration: newFees.totalDuration || newFees.durationMin || 0,
    distanceFormatted: newFees.distanceFormatted || '-',
    durationFormatted: newFees.durationFormatted || '-',
    distanceCost: newFees.distanceCost || newFees.distanceFee || 0,
    weightSurcharge: newFees.weightSurcharge || newFees.weightFee || 0,
    deliveryCost: newFees.deliveryCost || newFees.deliveryFee || 0,
    paymentFee: newFees.paymentFee || newFees.serviceFee || 0,
    currency: newFees.currency || 'XOF'
  };
}

// Watch for changes in filledData.fees and update local refs
watch(
  () => props.filledData?.fees,
  (newFees) => {
    if (newFees) {
      updateFees(newFees);
    }
  },
  { immediate: true, deep: true }
);

/**
 * Trigger dynamic fee recalculation
 */
function triggerRecalculation(immediate = false) {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  if (immediate) {
    executeRecalculation();
  } else {
    debounceTimer = setTimeout(() => {
      executeRecalculation();
    }, 350);
  }
}

function executeRecalculation() {
  isCalculatingFees.value = true;

  const srcDstLocation = props.filledData?.srcDstLocation;
  const packageType = props.filledData?.packageType;
  const deliveryType = props.filledData?.deliveryType;
  const distanceKm =
    feesData.value.totalDistance ||
    props.filledData?.fees?.distanceKm ||
    props.filledData?.distanceKm ||
    0;
  const durationMin =
    feesData.value.totalDuration ||
    props.filledData?.fees?.durationMin ||
    props.filledData?.durationMin ||
    0;

  const merchandisePrice = escrow.value.enabled
    ? Number(escrow.value.merchandisePrice) || 0
    : 0;
  const deliveryFeePayer = escrow.value.enabled
    ? escrow.value.deliveryFeePayer || 'sender'
    : 'sender';
  const paymentLinkEnabled = Boolean(escrow.value.enabled);
  const userId = props.filledData?.userId || getUserId();

  console.log('[SummaryStep] Emitting FEES_CALCULATE for dynamic recalculation:', {
    merchandisePrice,
    deliveryFeePayer,
    paymentLinkEnabled,
    distanceKm
  });

  bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATE, {
    srcDstLocation,
    packageType,
    deliveryType,
    distanceKm,
    durationMin,
    paymentLinkEnabled,
    merchandisePrice,
    deliveryFeePayer,
    userId,
    metadata: {
      wallet: `wallet_${userId}`,
      merchandisePrice,
      deliveryFeePayer,
      paymentLinkEnabled
    }
  });
}

function toggleEscrow() {
  escrow.value.enabled = !escrow.value.enabled;
  triggerRecalculation(true);
}

function setDeliveryFeePayer(payer) {
  if (escrow.value.deliveryFeePayer !== payer) {
    escrow.value.deliveryFeePayer = payer;
    triggerRecalculation(true);
  }
}

function onMerchandisePriceInput() {
  triggerRecalculation(false);
}

const onFeesCalculated = (newFees) => {
  isCalculatingFees.value = false;
  if (newFees) {
    updateFees(newFees);
  }
};

const onFeesCalculateError = (err) => {
  isCalculatingFees.value = false;
  console.error('[SummaryStep] Fees calculation error:', err);
};

function onMapReady() {
  mapReady.value = true;
  updateMapDisplay();
}

async function updateMapDisplay() {
  if (!mapReady.value || !mapRef.value) return;

  const srcPos = props.filledData?.srcDstLocation?.srcPosition?.coordinates;
  const dstPos = props.filledData?.srcDstLocation?.dstPosition?.coordinates;
  const allCheckpoints = props.filledData?.srcDstLocation?.checkpoints || [];

  if (!srcPos || !dstPos) return;

  // Nettoyer les anciens marqueurs
  mapRef.value.cleanRoute('package-summary');

  // Ajouter les marqueurs et tracer la route
  mapRef.value.setMarker(
    { position: srcPos, content: 'D', iconType: 'pickup', options: { title: 'Départ' } },
    'package-summary',
    'src-marker'
  );

  mapRef.value.setMarker(
    { position: dstPos, content: 'A', iconType: 'destination', options: { title: 'Arrivée' } },
    'package-summary',
    'dst-marker'
  );

  allCheckpoints.forEach((checkpoint, index) => {
    if (checkpoint?.coordinates) {
      mapRef.value.setMarker(
        {
          position: checkpoint.coordinates,
          content: String(index + 1),
          iconType: 'checkpoint',
          options: { title: `Détour ${index + 1}` }
        },
        'package-summary',
        `checkpoint-${index}-marker`
      );
    }
  });

  // Tracer la route
  const checkpointCoords = allCheckpoints.filter(c => c?.coordinates).map(c => c.coordinates);
  if (checkpointCoords.length > 0) {
    await mapRef.value.traceRouteWithPoints('package-summary', srcPos, dstPos, checkpointCoords);
  } else {
    await mapRef.value.traceRouteWithPoints('package-summary', srcPos, dstPos);
  }

  // Centrer sur le départ
  mapRef.value.centerOnPosition(srcPos);
}

onMounted(() => {
  bus.on(constants.EVENTS.PACKAGES.FEES_CALCULATED, onFeesCalculated);
  bus.on(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, onFeesCalculateError);
  updateMapDisplay();
});

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
  bus.off(constants.EVENTS.PACKAGES.FEES_CALCULATED, onFeesCalculated);
  bus.off(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, onFeesCalculateError);
  if (mapRef.value) {
    mapRef.value.cleanRoute('package-summary');
  }
});

const parcelDetails = computed(() => {
  return props.filledData?.parcelDetails || props.filledData?.packageType?.parcelDetails || {};
});

const formattedDate = computed(() => {
  if (!props.filledData?.startDate) return '-';
  const date = new Date(props.filledData.startDate);
  return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
});

const formattedTime = computed(() => {
  if (!props.filledData?.startDate) return '-';
  const date = new Date(props.filledData.startDate);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
});

// Use fees calculated by server (from local reactive refs)
const totalDistance = computed(() => feesData.value.totalDistance);
const deliveryCost = computed(() => feesData.value.deliveryCost);
const distanceCost = computed(() => feesData.value.distanceCost);
const weightSurcharge = computed(() => feesData.value.weightSurcharge);
const paymentFee = computed(() => feesData.value.paymentFee);
const totalAmount = computed(() => feesData.value.totalAmount);

// Escrow computed amounts
const buyerDeliveryCost = computed(() => {
  if (!escrow.value.enabled) return 0;
  if (escrow.value.deliveryFeePayer === 'receiver') {
    return (
      feesData.value.customerDeliveryCost ||
      feesData.value.deliveryFee ||
      feesData.value.deliveryCost ||
      0
    );
  }
  return 0;
});

const buyerToPay = computed(() => {
  if (!escrow.value.enabled) return 0;
  return (Number(escrow.value.merchandisePrice) || 0) + buyerDeliveryCost.value;
});

const senderToPay = computed(() => {
  if (!escrow.value.enabled) {
    return totalAmount.value;
  }
  if (escrow.value.deliveryFeePayer === 'receiver') {
    return feesData.value.merchantDeliveryCost || 0;
  }
  return (
    feesData.value.merchantDeliveryCost ||
    feesData.value.deliveryFee ||
    totalAmount.value ||
    0
  );
});

const remainingAmount = computed(() => {
  if (!escrow.value.enabled) return 0;
  const merchandise = Number(escrow.value.merchandisePrice) || 0;
  if (
    feesData.value.merchantPayout !== undefined &&
    feesData.value.merchantPayout !== null &&
    feesData.value.merchantPayout > 0
  ) {
    return feesData.value.merchantPayout;
  }
  const fees =
    escrow.value.deliveryFeePayer === 'sender'
      ? feesData.value.merchantDeliveryCost || (deliveryCost.value + paymentFee.value)
      : 0;
  return merchandise - fees;
});

/**
 * Get current userId from store or localStorage
 */
function getUserId() {
  // Try to get from user store first
  const userStore = window.$stores?.user;
  if (userStore?.state?.current?.id) {
    return userStore.state.current.id;
  }

  // Fallback to localStorage
  const userId = localStorage.getItem('user_id');
  if (userId) {
    return userId;
  }

  // Generate a temporary userId if none exists
  const tempUserId = 'temp-user-' + Date.now();
  localStorage.setItem('user_id', tempUserId);
  return tempUserId;
}

function getStepResult() {
  return {
    ...props.filledData,
    userId: props.filledData?.userId || getUserId(),
    fees: {
      ...feesData.value,
      totalAmount:
        escrow.value.enabled && escrow.value.deliveryFeePayer === 'receiver'
          ? senderToPay.value
          : totalAmount.value
    },
    // Escrow / lien de paiement sécurisé
    paymentLinkEnabled: escrow.value.enabled,
    merchandisePrice: escrow.value.enabled
      ? Number(escrow.value.merchandisePrice) || 0
      : 0,
    deliveryFeePayer: escrow.value.enabled
      ? escrow.value.deliveryFeePayer || 'sender'
      : 'sender',
    remainingAmount: remainingAmount.value,
    merchantPayout: remainingAmount.value,
    senderToPay: senderToPay.value,
    buyerToPay: buyerToPay.value
  };
}

defineExpose({
  getStepResult
});
</script>
